import { supabase } from '@/integrations/supabase/client';
import { setStepImageOverrides } from './step-images';

let loaded = false;
let loadingPromise: Promise<void> | null = null;

/**
 * Registers runtime image overrides for STEP_IMAGES.
 *
 * Resolution order per stepKey:
 *   1. step_media_overrides.active_image_url  ← admin-pinned in /admin/videos
 *   2. Latest non-starter file in `video-frames` bucket
 *      (newest wins; starter frames ignored)
 *
 * Idempotent — call once at app startup.
 */
export async function loadLovableImageOverrides(): Promise<void> {
  if (loaded) return;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const map: Record<string, string> = {};

    // 1) Auto-pick latest tail frame from storage (lowest priority)
    const { data: files, error: listErr } = await supabase.storage
      .from('video-frames')
      .list('', { limit: 1000, sortBy: { column: 'created_at', order: 'asc' } });

    if (listErr) {
      console.warn('[lovable-image-overrides] storage list failed', listErr);
    } else {
      for (const f of files ?? []) {
        const m = f.name.match(/^(.+?)(-starter)?-\d+\.(?:jpg|jpeg|png|webp)$/i);
        if (!m) continue;
        if (m[2]) continue; // skip starter frames
        const stepKey = m[1];
        const { data: pub } = supabase.storage.from('video-frames').getPublicUrl(f.name);
        map[stepKey] = pub.publicUrl; // ascending order ⇒ later writes win
      }
    }

    // 2) Admin-pinned active images take precedence
    const { data: pinned, error: pinErr } = await supabase
      .from('step_media_overrides')
      .select('step_key, active_image_url')
      .not('active_image_url', 'is', null);

    if (pinErr) {
      console.warn('[lovable-image-overrides] pinned load failed', pinErr);
    } else {
      for (const row of pinned ?? []) {
        if (row.step_key && row.active_image_url) {
          map[row.step_key] = row.active_image_url;
        }
      }
    }

    setStepImageOverrides(map);
    loaded = true;
  })();

  return loadingPromise;
}
